/**
 * "What Clay did" (plan 2026-09-27-003, U6): the four-row checklist from
 * `classifyRepStatus` (V3's own `checks` block when it sent one, otherwise
 * derived only as far as the data allows), the "Result:" line, an "Open run
 * in Clay" link to the V3 workflow page, and the run id as tertiary text.
 *
 * A failed-to-start row never reached Clay: no link. Its raw `row.error` —
 * an internal log tag, an HTTP status, sometimes a raw JSON body, sometimes a
 * Salesforce record id — is gated behind "Details (admin)", shown only to a
 * launcher admin (`isLauncherAdmin`, auth-scoped so impersonation can't flip
 * it) or while an impersonation session is active. This DELIBERATELY reverses
 * the earlier "every rep can see it, it's the only place they can" design
 * (`plans/2026-09-27-003-dispatch-log-redesign-plan.md:218`): a real prod row
 * rendered `[launcher.dispatch] Clay routine run could not be started for
 * member 00va700000I7T2DAAU: http: HTTP 429: {"message":"Rate limit
 * exceeded..."}` straight to a rep — exactly the internals/status-code/raw-JSON
 * leak `mini-apps/playbooks/CLAUDE.md` forbids. The plain `Result:` line
 * (from the one classifier, `dispatch-rep-status.ts`) is unaffected and stays
 * visible to everyone.
 */
import { Check, DotsThree, Minus, WarningCircle } from '@phosphor-icons/react';
import { Icon } from '@clay-run/glaze/icon';
import { Text } from '@clay-run/glaze/text';
import { TextLink } from '@clay-run/glaze/textlink';

import { useCurrentUser, useImpersonationState } from '../../../../_shared/hooks';
import { isLauncherAdmin } from '../../../config-page-logic';
import type { RepCheckState, RepCheckView, RepStatusView } from '../../../dispatch-rep-status';
import type { PlaybooksDispatchLogRow } from '../../../schemas';

import { DetailPanel } from './DetailPanel';

const CHECK_LABELS: ReadonlyArray<readonly [keyof RepStatusView['checks'], string]> = [
  ['qualification', 'Qualification'],
  ['emailValidation', 'Email validation'],
  ['copyQa', 'Copy QA'],
  ['delivery', 'Delivery'],
];

const STATE_WORD: Record<RepCheckState, string> = {
  pass: 'Passed',
  stop: 'Stopped',
  skip: 'Skipped',
  error: 'Errored',
  wait: 'Waiting',
};

/** The round state marker (prototype `.ic`). Semantic tokens only. */
function StateIcon({ state }: { state: RepCheckState }) {
  const base = 'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full';
  switch (state) {
    case 'pass':
      return (
        <span className={`${base} bg-bg-green text-content-success`}>
          <Icon type={Check} size="sm" />
        </span>
      );
    case 'stop':
      return (
        <span className={`${base} bg-bg-tertiary text-content-secondary`}>
          <Icon type={Minus} size="sm" />
        </span>
      );
    case 'error':
      return (
        <span className={`${base} bg-bg-red text-content-danger`}>
          <Icon type={WarningCircle} size="sm" />
        </span>
      );
    case 'wait':
      return (
        <span className={`${base} bg-bg-blue text-content-action`}>
          <Icon type={DotsThree} size="sm" />
        </span>
      );
    case 'skip':
      return <span className={`${base} border border-dashed border-border-primary`} />;
  }
}

function CheckRow({ label, check }: { label: string; check: RepCheckView }) {
  return (
    <li className="flex gap-2 border-b border-border-tertiary py-1.5 last:border-b-0">
      <StateIcon state={check.state} />
      <span className="sr-only">{`${STATE_WORD[check.state]}: `}</span>
      <div className="min-w-0">
        <Text size="xs" weight="medium">
          {label}
        </Text>
        <Text size="xs" variant="tertiary">
          {check.reason}
        </Text>
      </div>
    </li>
  );
}

export function WhatClayDidPanel({
  row,
  view,
  clayRunUrl,
  clayRunUrlKind,
}: {
  row: PlaybooksDispatchLogRow;
  view: RepStatusView;
  clayRunUrl: string | null;
  /** What `clayRunUrl` opens; absent (an older server) reads as the workflow page. */
  clayRunUrlKind?: 'run' | 'workflow';
}) {
  const { roles } = useCurrentUser();
  const { active: impersonating } = useImpersonationState();
  const canSeeRawError = isLauncherAdmin(roles) || impersonating;
  const reachedClay = view.status !== 'failed_to_start';
  const runId = view.routineRunId ?? view.workflowRunId;
  return (
    <DetailPanel title="What Clay did">
      <ul className="flex flex-col">
        {CHECK_LABELS.map(([key, label]) => (
          <CheckRow key={key} label={label} check={view.checks[key]} />
        ))}
      </ul>
      <div className="mt-2 border-t border-border-secondary pt-2">
        <Text size="xs">
          <span className="text-content-tertiary">Result: </span>
          {view.result}
        </Text>
        {view.status === 'failed_to_start' && row.error !== null && canSeeRawError && (
          <details className="mt-1">
            <summary className="cursor-pointer text-xs text-content-tertiary">Details (admin)</summary>
            <Text size="xs" variant="tertiary" className="break-words">
              {row.error}
            </Text>
          </details>
        )}
        {reachedClay && clayRunUrl !== null && (
          <div className="mt-2">
            <TextLink href={clayRunUrl} target="_blank" rel="noopener noreferrer" size="xs">
              {clayRunUrlKind === 'run' ? 'Open run in Clay ↗' : 'Open workflow in Clay ↗'}
            </TextLink>
          </div>
        )}
        {reachedClay && runId !== null && canSeeRawError && (
          <Text size="xs" variant="placeholder" className="mt-1 break-all">
            {`Run ${runId}`}
          </Text>
        )}
      </div>
    </DetailPanel>
  );
}
